import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Edit, Trash2, ChevronDown, ChevronUp, Search, CheckCircle, HelpCircle, XCircle, Circle, X } from 'lucide-react';
import axios from 'axios';
import { formatInTimeZone } from 'date-fns-tz';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import EventGroups from '../components/EventGroups';
import { formatAsEasternTime, formatAsLocalTime, formatAsEasternDate } from '../utils/timezone';

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signupLoading, setSignupLoading] = useState(false);
  const [userSignup, setUserSignup] = useState(null);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteDiscordEvent, setDeleteDiscordEvent] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [signupData, setSignupData] = useState({
    status: 'confirmed',
    notes: ''
  });
  const [signupSearchTerm, setSignupSearchTerm] = useState('');
  const [signupsCollapsed, setSignupsCollapsed] = useState(false);
  const [editingSignup, setEditingSignup] = useState(null); // Track which signup we're editing

  // Check if user can see notes (creators and admins only)
  const canSeeNotes = user?.role === 'creator' || user?.role === 'admin';



  // Filter signups based on search term
  const filteredSignups = event?.signups?.filter(signup => {
    if (!signupSearchTerm) return true;
    const searchLower = signupSearchTerm.toLowerCase();
    return (
      signup.username?.toLowerCase().includes(searchLower) ||
      signup.status?.toLowerCase().includes(searchLower) ||
      signup.notes?.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Sort signups: current user first, then by status priority
  const sortedSignups = [...filteredSignups].sort((a, b) => {
    // Current user always comes first
    if (a.user_id === event?.currentUserId) return -1;
    if (b.user_id === event?.currentUserId) return 1;
    
    // Then sort by status priority: confirmed > late > tentative > declined
    const statusPriority = { confirmed: 1, late: 2, tentative: 3, declined: 4 };
    const aPriority = statusPriority[a.status] || 5;
    const bPriority = statusPriority[b.status] || 5;
    
    return aPriority - bPriority;
  });

  // Initialize signup modal with existing data when editing
  const openSignupModal = (existingSignup = null) => {
    if (existingSignup) {
      setSignupData({
        status: existingSignup.status,
        notes: existingSignup.notes || ''
      });
      setEditingSignup(existingSignup); // Track which signup we're editing
    } else {
      setSignupData({
        status: 'confirmed',
        notes: ''
      });
      setEditingSignup(null);
    }
    setShowSignupModal(true);
  };

  const fetchEventDetails = useCallback(async () => {
    try {
          const response = await axios.get(`/api/events/${eventId}`);
      setEvent(response.data);
      
      // Find user's signup if it exists
      const userSignup = response.data.signups.find(
        signup => signup.user_id === response.data.currentUserId
      );
  
      setUserSignup(userSignup || null);
    } catch (error) {
      console.error('Failed to fetch event details:', error);
      if (error.response?.status === 404) {
        navigate('/events');
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  const handleSignup = async () => {
    setSignupLoading(true);
    try {
      if (editingSignup && (user?.role === 'admin' || user?.role === 'creator')) {
        // Editing another user's signup (admin/creator only)
        const response = await axios.put(`/api/events/${eventId}/signups/${editingSignup.id}`, signupData);
      } else if (editingSignup && editingSignup.user_id === event?.currentUserId) {
        // Editing own signup
        const response = await axios.put(`/api/events/${eventId}/signups/${editingSignup.id}`, signupData);
      } else {
        // Creating new signup
        const response = await axios.post(`/api/events/${eventId}/signup`, signupData);
      }
      
      setShowSignupModal(false);
      setEditingSignup(null);
      
      // Refresh event data
      await fetchEventDetails();
    } catch (error) {
      console.error('Failed to sign up:', error);
      alert('Failed to sign up for event. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const handleRemoveSignup = async () => {
    setSignupLoading(true);
    try {
      await axios.delete(`/api/events/${eventId}/signup`);
      fetchEventDetails();
      setUserSignup(null);
    } catch (error) {
      console.error('Failed to remove signup:', error);
    } finally {
      setSignupLoading(false);
    }
  };



  // Remove the old complex functions - we now use the simplified utility functions
  // const formatEventDate = (dateString) => { ... }
  // const formatEventTime = (dateString) => { ... }
  // const formatLocalTime = (dateString) => { ... }

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 dark:text-green-400';
      case 'late': return 'text-yellow-600 dark:text-yellow-400';
      case 'tentative': return 'text-orange-600 dark:text-orange-400';
      case 'declined': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle size={16} />;
      case 'tentative': return <HelpCircle size={16} />;
      case 'late': return <Clock size={16} />;
      case 'declined': return <XCircle size={16} />;
      default: return <Circle size={16} />;
    }
  };

  const getStatusGlow = (status) => {
    switch (status) {
      case 'confirmed': return 'shadow-green-200 dark:shadow-green-800';
      case 'late': return 'shadow-yellow-200 dark:shadow-yellow-800';
      case 'tentative': return 'shadow-orange-200 dark:shadow-orange-800';
      case 'declined': return 'shadow-red-200 dark:shadow-red-800';
      default: return 'shadow-gray-200 dark:shadow-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Event not found</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">The event you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/events')}
          className="btn btn-primary mt-4"
        >
          Back to Events
        </button>
      </div>
    );
  }

  const canEdit = event.currentUserRole === 'admin' || event.currentUserRole === 'creator';

  return (
    <div className="max-w-6xl mx-auto">
      {/* Banner */}
      <div className="mb-6">
        <img 
          src="/botcbanner.png" 
          alt="Blood on the Clocktower" 
          className="w-full h-32 object-cover rounded-lg shadow-lg"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{event.title}</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Created by {event.creator_name || 'Unknown Creator'}
              </p>
            </div>
            
            {/* Your Status - Inline */}
            <div className="flex items-center space-x-2">
              {userSignup ? (
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(userSignup.status)}`}>
                  {getStatusIcon(userSignup.status)} {userSignup.status}
                </div>
              ) : (
                <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  Not signed up
                </div>
              )}
              {userSignup && (
                <button
                  onClick={handleRemoveSignup}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Remove signup"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          
          <div className="flex space-x-2">
            {canEdit && (
              <>
                <button
                  onClick={() => navigate(`/events/${eventId}/edit`)}
                  className="btn btn-secondary flex items-center space-x-2"
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </button>

                <button
                  onClick={() => {
                    // Set deleteDiscordEvent to true by default if Discord event exists
                    setDeleteDiscordEvent(!!event.discord_event_id);
                    setShowDeleteModal(true);
                  }}
                  className="btn btn-danger flex items-center space-x-2"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Event Details - Compact */}
        <div className="lg:col-span-1">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Event Info</h2>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Date</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatAsEasternDate(new Date(event.date))} Eastern Time
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Start Time</p>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatAsEasternTime(new Date(event.date))} Eastern Time
                    </p>
                    {formatAsLocalTime(new Date(event.date), Intl.DateTimeFormat().resolvedOptions().timeZone)} your local time
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Duration</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {Math.round((event.duration / 60) * 10) / 10} hours
                  </p>
                </div>
              </div>

              {event.location && (
                <div className="flex items-center space-x-3">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Location</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {event.location}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {event.description && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Description</h3>
                <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>
            )}

            {/* Storytellers */}
            {event.storytellers && event.storytellers.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Storytellers</h3>
                <div className="space-y-2">
                  {event.storytellers.map((storyteller) => (
                    <div key={storyteller.id} className="flex items-center space-x-2 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-2">
                      {storyteller.avatar && storyteller.discord_id && (
                        <img
                          src={`https://cdn.discordapp.com/avatars/${storyteller.discord_id}/${storyteller.avatar}.png`}
                          alt={storyteller.username}
                          className="w-6 h-6 rounded-full border-2 border-purple-300 dark:border-purple-600"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1">
                        <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                          {storyteller.username || 'Unknown User'}
                        </span>
                        {storyteller.role && (
                          <span className="text-xs text-purple-600 dark:text-purple-400 ml-1">
                            ({storyteller.role})
                          </span>
                        )}
                      </div>
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Signups - Main Focus */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Signups</h2>
              <button
                onClick={() => setSignupsCollapsed(!signupsCollapsed)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                {signupsCollapsed ? (
                  <ChevronDown size={20} />
                ) : (
                  <ChevronUp size={20} />
                )}
              </button>
            </div>
            
            {!signupsCollapsed && (
              <>
                {/* Quick Signup Action */}
                {!userSignup && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100">Ready to join?</h3>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          Sign up for this event to let everyone know you're coming!
                        </p>
                      </div>
                      <button
                        onClick={() => openSignupModal()}
                        className="btn btn-primary"
                      >
                        Sign Up Now
                      </button>
                    </div>
                  </div>
                )}

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search signups..."
                      value={signupSearchTerm}
                      onChange={(e) => setSignupSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                {/* Signup Summary */}
                <div className="flex space-x-4 mb-4 text-sm">
                  <span className="flex items-center space-x-1">
                    <CheckCircle size={16} className="text-green-500" />
                    <span className="text-green-600 dark:text-green-400">
                      {event?.signups?.filter(s => s.status === 'confirmed').length || 0} Confirmed
                    </span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Clock size={16} className="text-yellow-500" />
                    <span className="text-yellow-600 dark:text-yellow-400">
                      {event?.signups?.filter(s => s.status === 'late').length || 0} Late
                    </span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <HelpCircle size={16} className="text-orange-500" />
                    <span className="text-orange-600 dark:text-orange-400">
                      {event?.signups?.filter(s => s.status === 'tentative').length || 0} Tentative
                    </span>
                  </span>
                </div>

                {/* Signups List */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                  {sortedSignups.length > 0 ? (
                    sortedSignups.map((signup) => (
                      <div
                        key={signup.id}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 ${
                          signup.user_id === event?.currentUserId 
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400 shadow-lg shadow-blue-500/20' 
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}
                      >
                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                          {signup.avatar && signup.discord_id ? (
                            <img
                              src={`https://cdn.discordapp.com/avatars/${signup.discord_id}/${signup.avatar}.png`}
                              alt={signup.username}
                              className="w-6 h-6 rounded-full flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                              {signup.username || 'Unknown User'}
                              {signup.user_id === event?.currentUserId && (
                                <span className="ml-1 text-blue-600 dark:text-blue-400">(You)</span>
                              )}
                            </div>
                            {canSeeNotes && signup.notes ? (
                              <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                {signup.notes}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          <div className={`p-1 rounded-full ${getStatusGlow(signup.status)}`}>
                            {getStatusIcon(signup.status)}
                          </div>
                          {(signup.user_id === event?.currentUserId || canEdit) && (
                            <button
                              onClick={() => openSignupModal(signup)}
                              className="text-blue-500 hover:text-blue-700 p-1"
                              title={signup.user_id === event?.currentUserId ? "Edit your signup" : "Edit signup"}
                            >
                              <Edit size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8 text-gray-500">
                      {signupSearchTerm ? 'No signups match your search' : 'No signups yet'}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Signup Action for signed up users */}
            {userSignup && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => openSignupModal(userSignup)}
                  className="btn btn-secondary w-full"
                >
                  Update My Signup
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Groups - Added spacing */}
      <div className="mt-8">
        <EventGroups 
          eventId={eventId} 
          eventTitle={event?.title} 
        />
      </div>

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {editingSignup ? 
                (editingSignup.user_id === event?.currentUserId ? 'Update Signup' : `Edit ${editingSignup.username}'s Signup`) : 
                'Sign Up for Event'
              }
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={signupData.status}
                  onChange={(e) => setSignupData({ ...signupData, status: e.target.value })}
                  className="input w-full"
                >
                  <option value="confirmed">✅ Confirmed</option>
                  <option value="late">⏰ Late</option>
                  <option value="tentative">🤔 Tentative</option>
                  <option value="declined">❌ Declined</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={signupData.notes}
                  onChange={(e) => setSignupData({ ...signupData, notes: e.target.value })}
                  className="input w-full h-24 resize-none"
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowSignupModal(false)}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              {editingSignup && editingSignup.user_id === event?.currentUserId && (
                <button
                  onClick={handleRemoveSignup}
                  disabled={signupLoading}
                  className="btn btn-danger flex-1"
                >
                  {signupLoading ? 'Removing...' : 'Delete Signup'}
                </button>
              )}
              <button
                onClick={handleSignup}
                disabled={signupLoading}
                className="btn btn-primary flex-1"
              >
                {signupLoading ? 'Saving...' : (editingSignup ? 'Update' : 'Sign Up')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Delete Event
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to delete "{event.title}"? This action cannot be undone.
              </p>
              
              {event.discord_event_id ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="deleteDiscordEvent"
                      checked={deleteDiscordEvent}
                      onChange={(e) => setDeleteDiscordEvent(e.target.checked)}
                      className="mt-1"
                    />
                    <div>
                      <label htmlFor="deleteDiscordEvent" className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Also delete Discord event
                      </label>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                        This will remove the associated Discord event from your server as well.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        No Discord event associated
                      </p>
                      <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                        This event is not linked to a Discord event, so no Discord deletion is needed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    setDeleting(true);
                    await axios.delete(`/api/events/${eventId}`, {
                      data: { deleteDiscordEvent }
                    });
                    navigate('/events');
                  } catch (error) {
                    console.error('Failed to delete event:', error);
                    alert('Failed to delete event. Please try again.');
                  } finally {
                    setDeleting(false);
                    setShowDeleteModal(false);
                  }
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail; 